import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from 'react-router-dom';

const VISITOR_ID_KEY = 'lt_visitor_id';
const SESSION_COUNT_KEY = 'lt_session_count';
const LAST_ACTIVITY_KEY = 'lt_last_activity';
const DISMISSED_KEY = 'lt_promo_dismissed';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface PromoBannerSettings {
  id: string;
  is_enabled: boolean;
  discount_code_id: string | null;
  display_locations: string[];
  target_audience: string;
  min_sessions: number;
  banner_message: string | null;
  background_color: string;
  text_color: string;
}

interface DiscountCode {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
  expires_at: string | null;
  usage_limit: number | null;
  usage_count: number;
}

export function usePromoBanner() {
  const location = useLocation();
  const [sessionCount, setSessionCount] = useState(1);
  const [isDismissed, setIsDismissed] = useState(false);

  // Generate or retrieve visitor ID
  const getVisitorId = useCallback(() => {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  }, []);

  // Track session
  useEffect(() => {
    const visitorId = getVisitorId();
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const storedSessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '1', 10);
    const now = Date.now();

    // Check if this is a new session (30+ min since last activity)
    const isNewSession = !lastActivity || (now - parseInt(lastActivity, 10)) > SESSION_TIMEOUT;

    if (isNewSession) {
      const newCount = storedSessionCount + (lastActivity ? 1 : 0);
      localStorage.setItem(SESSION_COUNT_KEY, newCount.toString());
      setSessionCount(newCount);

      // Sync to database (fire and forget)
      supabase
        .from('user_session_counts')
        .upsert({
          visitor_id: visitorId,
          session_count: newCount,
          last_seen_at: new Date().toISOString(),
        }, { onConflict: 'visitor_id' })
        .then(() => {});
    } else {
      setSessionCount(storedSessionCount);
    }

    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, [getVisitorId]);

  // Check if banner was dismissed
  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissTime < DISMISS_DURATION) {
        setIsDismissed(true);
      } else {
        localStorage.removeItem(DISMISSED_KEY);
      }
    }
  }, []);

  // Fetch promo banner settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['promo-banner-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promo_banner_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as PromoBannerSettings | null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch associated discount code if enabled
  const { data: discountCode, isLoading: codeLoading } = useQuery({
    queryKey: ['promo-discount-code', settings?.discount_code_id],
    queryFn: async () => {
      if (!settings?.discount_code_id) return null;

      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('id', settings.discount_code_id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      return data as DiscountCode | null;
    },
    enabled: !!settings?.discount_code_id,
    staleTime: 5 * 60 * 1000,
  });

  // Determine current location type
  const getCurrentLocationType = (): 'homepage' | 'app' | null => {
    if (location.pathname === '/') return 'homepage';
    if (location.pathname.startsWith('/dashboard') || 
        location.pathname.startsWith('/tribute/')) return 'app';
    return null;
  };

  // Check if code is valid (not expired, not at usage limit)
  const isCodeValid = (code: DiscountCode | null): boolean => {
    if (!code || !code.is_active) return false;
    if (code.expires_at && new Date(code.expires_at) < new Date()) return false;
    if (code.usage_limit && code.usage_count >= code.usage_limit) return false;
    return true;
  };

  // Check if user meets audience criteria
  const meetsAudienceCriteria = (): boolean => {
    if (!settings) return false;

    switch (settings.target_audience) {
      case 'everyone':
        return true;
      case 'returning':
        return sessionCount > 1;
      case 'after_sessions':
        return sessionCount >= (settings.min_sessions || 1);
      default:
        return true;
    }
  };

  // Should banner be shown?
  const shouldShowBanner = (): boolean => {
    if (settingsLoading || codeLoading) return false;
    if (!settings?.is_enabled) return false;
    if (isDismissed) return false;
    if (!isCodeValid(discountCode || null)) return false;

    const locationType = getCurrentLocationType();
    if (!locationType) return false;
    if (!settings.display_locations.includes(locationType)) return false;

    if (!meetsAudienceCriteria()) return false;

    return true;
  };

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setIsDismissed(true);
  }, []);

  // Format the banner message with code and discount
  const getFormattedMessage = (): string => {
    if (!settings?.banner_message || !discountCode) {
      // Default message
      const discountText = discountCode?.discount_type === 'percentage'
        ? `${discountCode.discount_value}%`
        : `$${discountCode?.discount_value}`;
      return `Use code ${discountCode?.code} for ${discountText} off!`;
    }

    let message = settings.banner_message;
    message = message.replace('{CODE}', discountCode.code);
    
    const discountText = discountCode.discount_type === 'percentage'
      ? `${discountCode.discount_value}%`
      : `$${discountCode.discount_value}`;
    message = message.replace('{DISCOUNT}', discountText);

    return message;
  };

  return {
    showBanner: shouldShowBanner(),
    settings,
    discountCode,
    dismissBanner,
    formattedMessage: discountCode ? getFormattedMessage() : '',
    isLoading: settingsLoading || codeLoading,
  };
}
